import { ColumnTypes, ITableColumn } from '@makyoapp/makyo-sdk/models/Table'

import { Cell, ICell } from '.'
import { DateTimeCell } from './dateTime'
import { FunctionCell } from './function'
import { MathCell } from './math'
import { NumberCell } from './number'
import { TextCell } from './text'

export interface ILookupColumnSettings {
  connectionColumnID: string
  foreignColumnIDToShow: string
}

export interface ILookupColumn extends ITableColumn {
  Settings: ILookupColumnSettings
}

export interface ILookupCell extends ICell {
  settings: ILookupColumnSettings
  resolvedType: ColumnTypes
  resolvedSettings: Record<string, any>
  toValues: () => any[]
}

export class LookupCell implements ILookupCell {
  public value: Cell[] = []
  public settings: ILookupColumnSettings
  public resolvedType: ColumnTypes
  resolvedSettings: Record<string, any>
  timezone?: string
  
  constructor(
    value: Cell[] = [],
    settings: ILookupColumnSettings,
    resolvedType?: ColumnTypes,
    resolvedSettings?: Record<string, any>,
    timezone?: string
  ) {
    this.value = this.getFlatValue(value)
    this.settings = settings
    this.resolvedType = resolvedType ?? ColumnTypes.Text
    this.resolvedSettings = resolvedSettings ?? {}
    this.timezone = timezone
  }

  public getFlatValue(value: Cell[]): Cell[] {
    if (Array.isArray(value)) {
      return value.flat()
    }
    return value ?? []
  }

  // @note: add more column types if needed
  public toValues(): Cell[] {
    if (!Array.isArray(this.value)) {
      return []
    }

    switch (this.resolvedType) {
      case ColumnTypes.Number:
        return this.value.map((val) => new NumberCell(val as number, this.resolvedSettings as any).toString())
      case ColumnTypes.DateTime:
        return this.value.map((val) => new DateTimeCell(val as string, this.resolvedSettings as any, this.timezone).toString())
      case ColumnTypes.Math:
        return this.value.map((val) => new MathCell(val as any, this.resolvedSettings as any).toString())
      case ColumnTypes.Function:
        return this.value.map((val) => new FunctionCell(val as any, this.resolvedSettings as any).toString())
      default:
        return this.value.map((val) => val?.toString() || '')
    }
  }

  // @note: add more column types if needed
  public toReturnTypeCell(): ICell[] {
    switch (this.resolvedType) {
      case ColumnTypes.DateTime:
        return this.value.map((val) => new DateTimeCell(val, this.resolvedSettings as any, this.timezone))
      case ColumnTypes.Number:
        return this.value.map((val) => new NumberCell(val, this.resolvedSettings as any))
      case ColumnTypes.Math:
        return this.value.map((val) => new MathCell(val, this.resolvedSettings as any))
      case ColumnTypes.Function:
        return this.value.map((val) => new FunctionCell(val, this.resolvedSettings as any))
      default:
        return this.value.map((val) => new TextCell(val, null))
    }
  }

  public toString(): string {
    return this.toValues().join(', ')
  }

  public toDisplayString(): string {
    return this.toValues().join(', ')
  }

  public toCell<T extends Cell>(value: T): LookupCell {
    return this
  }

  public toMultipleDataCell<T extends Cell>(value: T[]): LookupCell {
    this.value = this.getFlatValue(value)
    return this
  }
}
